import React from 'react';
import { Campaign, ItemDraft, ManagerDetails } from '../types';
import { ItemForm } from './ItemForm';
import { CheckCircle, Loader2, AlertTriangle } from 'lucide-react';
import { asArray } from '../utils/asArray';
import { BrandChip } from './common/BrandChip';
import { SubmissionCode } from './common/SubmissionCode';

export interface BundleEntry {
  submissionId: string;
  brandId: string;
  brandName: string;
  status?: string;
  items: ItemDraft[];
  saving?: boolean;
}

interface ManagerSubmissionBundleViewProps {
  campaign: Campaign;
  entries: BundleEntry[];
  activeSubmissionId: string;
  onSelectTab: (submissionId: string) => void;
  onItemsChange: (submissionId: string, items: ItemDraft[]) => void;
  onSaveDraft: (submissionId: string) => void;
  onSubmitAll: () => void;
  submittingAll: boolean;
  managerDetails: ManagerDetails;
  setManagerDetails: React.Dispatch<React.SetStateAction<ManagerDetails>>;
  currencyCode?: string;
  submitResults?: Record<string, 'success' | 'error'>;
  onBack?: () => void;
}

export const ManagerSubmissionBundleView: React.FC<ManagerSubmissionBundleViewProps> = ({
  campaign,
  entries,
  activeSubmissionId,
  onSelectTab,
  onItemsChange,
  onSaveDraft,
  onSubmitAll,
  submittingAll,
  managerDetails,
  setManagerDetails,
  currencyCode,
  submitResults = {},
  onBack,
}) => {
  const safeEntries = asArray(entries);
  if (!safeEntries.length) return null;
  const activeEntry = safeEntries.find((e) => e.submissionId === activeSubmissionId) || safeEntries[0];
  const brandLine =
    safeEntries.length > 3
      ? `${safeEntries.slice(0, 3).map((e) => e.brandName).join(', ')} +${safeEntries.length - 3} more`
      : safeEntries.map((e) => e.brandName).join(', ');

  return (
    <div className="space-y-4 animate-fade-in max-w-3xl mx-auto text-sm">
      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] font-bold text-teal-600 uppercase mb-1">Campaign</p>
            <h2 className="text-xl font-extrabold text-zinc-900 leading-tight">{campaign.name}</h2>
            <p className="text-xs text-zinc-500 mt-1">{campaign.description}</p>
            <div className="mt-2 text-xs text-zinc-600 font-semibold">
              Applies to: <span className="text-zinc-900">{brandLine || 'N/A'}</span>
            </div>
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className="px-3 py-2 rounded-lg border border-zinc-200 text-xs font-bold text-zinc-700 bg-white hover:border-teal-200 hover:text-teal-700 transition"
            >
              ← Back
            </button>
          )}
        </div>
      </div>

      {safeEntries.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {safeEntries.map((entry) => {
            const result = submitResults[entry.submissionId];
            return (
              <button
                key={entry.submissionId}
                onClick={() => onSelectTab(entry.submissionId)}
                className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition ${
                  activeEntry.submissionId === entry.submissionId
                    ? 'border-teal-500 bg-teal-50 text-teal-800'
                    : 'border-zinc-200 bg-white text-zinc-600 hover:border-teal-200 hover:text-teal-700'
                }`}
              >
                <div className="flex items-center gap-2">
                  <BrandChip brandName={entry.brandName} size="xs" />
                  <SubmissionCode submissionId={entry.submissionId} brandName={entry.brandName} mode="suffix" />
                </div>
                {result === 'success' && <CheckCircle className="inline w-3 h-3 ml-2 text-emerald-600" />}
                {result === 'error' && <AlertTriangle className="inline w-3 h-3 ml-2 text-amber-500" />}
              </button>
            );
          })}
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-bold text-zinc-500 uppercase">Submitting for</p>
            <p className="text-lg font-bold text-zinc-900">{activeEntry.brandName}</p>
            <p className="text-[11px] text-zinc-500">
              <SubmissionCode submissionId={activeEntry.submissionId} brandName={activeEntry.brandName} />
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-bold text-zinc-500 uppercase">Manager</p>
            <p className="text-sm font-bold text-zinc-900">{managerDetails.name}</p>
            <p className="text-[11px] text-zinc-500 font-mono">ID: {managerDetails.fileNumber}</p>
          </div>
        </div>

        <ItemForm
          brandNames={[activeEntry.brandName]}
          items={activeEntry.items}
          setItems={(next) => {
            const resolved = typeof next === 'function' ? next(asArray(activeEntry.items)) : next;
            onItemsChange(activeEntry.submissionId, asArray(resolved));
          }}
          onBack={() => {}}
          onNext={() => {}}
          currencyCode={currencyCode}
          hideNav
        />

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-zinc-100 text-xs">
          <div className="text-zinc-500">
            Campaign: <span className="font-semibold text-zinc-800">{campaign.name}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onSaveDraft(activeEntry.submissionId)}
              className="px-3 py-1.5 rounded-lg border border-zinc-200 text-xs font-bold text-zinc-700 bg-white hover:border-teal-200 hover:text-teal-700 transition"
              disabled={activeEntry.saving || submittingAll}
            >
              {activeEntry.saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              onClick={onSubmitAll}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 shadow-sm transition disabled:opacity-70"
              disabled={submittingAll}
            >
              {submittingAll && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
